"use client";

import { cn } from "@shared/lib/cn";
import type { ShikiLang } from "@shared/lib/shiki";
import { Check, Copy } from "lucide-react";
import { useState } from "react";
import { Button } from "./button";
import { CodeBlock } from "./code-block";
import { toast } from "./snackbar";
import { Tabs2, TabsContent2, TabsList2, TabsTrigger2 } from "./tabs2";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

// ─── Public API ───────────────────────────────────────────────────────────────

export interface CodeSnippet2Tab {
  /** Unique tab identifier — used as the Tabs value. */
  id: string;
  /** Label shown in the tab strip. */
  label: string;
  /** Shiki grammar to use for highlighting. */
  lang: ShikiLang;
  /** The code string to display and copy. */
  code: string;
}

interface CodeSnippet2Props {
  tabs: CodeSnippet2Tab[];
  /** Tab id to activate initially. Defaults to the first tab. */
  defaultTab?: string;
  /** When true, copy is disabled and copyDisabledMessage is shown in a tooltip. */
  copyDisabled?: boolean;
  /** Shown when copyDisabled is true (e.g. capacity-blocked explanation). */
  copyDisabledMessage?: string;
  /**
   * Height for the code region. Defaults to a preferred 404px, clamped against
   * the viewport so short screens still leave room for the rest of the page
   * (header, title, key field, tabs, Continue). The white card fills this
   * region and scrolls overflow code inside.
   */
  codeRegionClassName?: string;
  /** Background for the code panel. Defaults to `bg-background`. */
  codeRegionBackgroundClassName?: string;
  className?: string;
}

const isTransparentBackgroundClass = (className: string) => /\/\d{1,3}$/.test(className);

// ─── Component ────────────────────────────────────────────────────────────────

const DEFAULT_COPY_DISABLED_MESSAGE =
  "Copy is unavailable until you have access. We will notify you when capacity is available.";

/**
 * Preferred code-region height: 404px, but never taller than the viewport minus
 * ~36rem reserved for surrounding onboarding chrome, and never shorter than 12rem.
 */
const DEFAULT_CODE_REGION_CLASS =
  "h-[min(404px,max(12rem,calc(100dvh-36rem)))] min-h-[min(404px,max(12rem,calc(100dvh-36rem)))]";

/**
 * Tabbed code-snippet panel (v2) with underline language subtabs and a
 * height-stabilized, internally scrolling code region. Used by onboarding;
 * migrate other call sites from CodeSnippet gradually.
 */
export const CodeSnippet2 = ({
  tabs,
  defaultTab,
  copyDisabled = false,
  copyDisabledMessage = DEFAULT_COPY_DISABLED_MESSAGE,
  codeRegionClassName = DEFAULT_CODE_REGION_CLASS,
  codeRegionBackgroundClassName = "bg-background",
  className,
}: CodeSnippet2Props) => {
  const [copied, setCopied] = useState(false);
  const firstId = tabs[0]?.id ?? "";
  const [activeTab, setActiveTab] = useState(defaultTab ?? firstId);

  if (tabs.length === 0) return null;

  const codeRegionUsesOpacityOverlay = isTransparentBackgroundClass(codeRegionBackgroundClassName);

  const handleCopy = async () => {
    if (copyDisabled) return;
    const tab = tabs.find((t) => t.id === activeTab) ?? tabs[0];
    if (!tab) return;

    try {
      await navigator.clipboard.writeText(tab.code);
      setCopied(true);
      toast.success("Code copied to clipboard.");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const copyButton = copyDisabled ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled
            className="size-8 bg-background/90 text-muted-foreground shadow-xs backdrop-blur-sm"
            aria-label="Copy code"
          >
            <Copy className="size-3.5" aria-hidden />
          </Button>
        </span>
      </TooltipTrigger>
      <TooltipContent sideOffset={4} className="max-w-xs text-balance">
        {copyDisabledMessage}
      </TooltipContent>
    </Tooltip>
  ) : (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="size-8 bg-background/90 text-muted-foreground shadow-xs backdrop-blur-sm hover:text-foreground"
      onClick={() => void handleCopy()}
      aria-label={copied ? "Copied" : "Copy code"}
    >
      {copied ? <Check className="size-3.5" aria-hidden /> : <Copy className="size-3.5" aria-hidden />}
    </Button>
  );

  return (
    <Tabs2
      value={activeTab}
      onValueChange={(value) => {
        setActiveTab(value);
        setCopied(false);
      }}
      className={cn("gap-0", className)}
    >
      {/* Language subtabs */}
      <div className="flex shrink-0 items-center gap-2 px-1">
        <TabsList2 variant="sub" className="min-w-0 flex-1">
          {tabs.map((tab) => (
            <TabsTrigger2 key={tab.id} value={tab.id} variant="sub">
              {tab.label}
            </TabsTrigger2>
          ))}
        </TabsList2>
      </div>

      {/* Code region: clamped height; code scrolls; copy pinned top-right */}
      <div
        className={cn(
          "relative h-full overflow-hidden rounded-control pt-3",
          codeRegionUsesOpacityOverlay ? "bg-background" : codeRegionBackgroundClassName,
          codeRegionClassName,
        )}
      >
        {codeRegionUsesOpacityOverlay ? (
          <div
            aria-hidden
            className={cn("pointer-events-none absolute inset-0 rounded-control", codeRegionBackgroundClassName)}
          />
        ) : null}
        <div className="absolute top-2 right-2 z-10">{copyButton}</div>
        {tabs.map((tab) => (
          <TabsContent2 key={tab.id} value={tab.id} className="relative z-[1] h-full overflow-auto">
            {/* Extra right padding so lines clear the pinned copy control */}
            <CodeBlock code={tab.code} lang={tab.lang} className="[&_pre]:pr-12" />
          </TabsContent2>
        ))}
      </div>
    </Tabs2>
  );
};
